// done-path-worker-output-rejection.ts — CLI 3.38.0-beta.2 (dogfood chat-drive
// run 2, F-72). How the done path turns a REJECTED worker output into a route.
//
// Before: every rejection was `escalate_waiting_human` ⇒ ticket `blocked` with
// `counted_failures` 0, and nothing in v1 re-dispatches a blocked ticket — a
// survey that returned one `proposed_tickets` entry, or a plan that proposed a
// ticket with `executor: "controller"`, left the mission one way out: abort.
// Those escalations copied the self-amend guard's mechanism (06c), not a
// safety argument.
//
// Now, fail CLOSED on an explicit allow-list (coordinator, beta.2 review):
//   - ALLOW-LISTED (output shape/validity the worker can fix — a done-path
//     receipt, or an applier refusal CODE): journal the receipt, then
//     `recordAttemptOutcome(task_failed)` ⇒ retry with `<receipt>: <reason>`
//     in the next prompt's "Lần trước bị từ chối vì" section, `blocked` once
//     `max_counted_failures` is reached.
//   - EVERYTHING ELSE escalates as before: the governance guards (the governed
//     party writing its own rules — a retry lets it iterate until something
//     passes) carry no code, and neither does any guard added later.
import { randomUUID } from "node:crypto";
import type { BlockedBy } from "./escalation-blocked-by.js";
import type { MissionStore } from "./mission-store.js";
import type { ProposalRejectionCode } from "./proposal-change-types.js";
import {
  type RetryConfig,
  type RetryDecision,
  type RetryState,
  recordAttemptOutcome,
} from "./retry-policy.js";

export type RejectionRoute = { kind: "retry_decision"; decision: RetryDecision };

/** Journal an `operation_result` receipt with no preceding intent (audit trail for rejections). */
export async function journalRejectionReceipt(
  store: MissionStore,
  outcome: string,
  remoteId?: string,
): Promise<void> {
  const operationId = randomUUID();
  await store.commit([
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        outcome,
        ...(remoteId === undefined ? {} : { remote_id: remoteId }),
      },
    },
  ]);
}

// A post-hoc rejection of an otherwise "done" attempt is not a worker/
// classifier-level ErrorCategory, so "task_failed" (the generic fallback) is
// the category (dogfooding P9b: the verdict must be journalable).
// `blockedBy` defaults to `other` — fail closed, never recovery-eligible.
export function escalate(
  state: RetryState,
  reason: string,
  evidence = reason,
  blockedBy: BlockedBy = "other",
): RejectionRoute {
  const decision: RetryDecision = {
    outcome: "escalate_waiting_human",
    state,
    reason,
    category: "task_failed",
    evidence,
    blocked_by: blockedBy,
  };
  return { kind: "retry_decision", decision };
}

/**
 * ALLOW-list, fail CLOSED: only these classes are retried. A receipt or code
 * missing here — every governance refusal, and any guard added later that
 * nobody listed — escalates to a human exactly as before F-72. Widening a list
 * is a reviewed change (pinned verbatim by the allow-list test).
 */
export const COUNTED_DONE_PATH_RECEIPTS: readonly string[] = Object.freeze([
  "proposed_tickets_rejected",
  "plan_done_without_feature_tickets",
]);
export const PROPOSAL_APPLY_RECEIPT = "proposal_apply_rejected";
/** Codes the applier attaches to a worker-fixable refusal (proposal-change-types.ts). */
export const COUNTED_PROPOSAL_REJECTION_CODES: readonly ProposalRejectionCode[] = Object.freeze([
  "schema",
  "executor",
  "adapter_mismatch",
  "missing_acceptance",
  "stale_revision",
  "unknown_target",
  "duplicate_id",
  "batch_collision",
  "dag",
  "dangling_dependency",
  "red_anchor_lost",
]);

/**
 * Audit F-3: journal reasons that start with one of these receipts quote
 * WORKER output (its own text, target names…). `resume`'s quota/session-limit
 * unblock must not read them, or a worker could plant "usage limit" text to
 * reset its own counted failures.
 */
export const WORKER_OUTPUT_REASON_PREFIXES: readonly string[] = Object.freeze([
  ...COUNTED_DONE_PATH_RECEIPTS,
  PROPOSAL_APPLY_RECEIPT,
  "plan_delegates_open_decision_to_worker_rejected",
]);

/** Uncounted receipts that are governance refusals (`blocked_by: governance`); others ⇒ `other`. */
const HUMAN_ONLY_GOVERNANCE_RECEIPTS: readonly string[] = Object.freeze([
  PROPOSAL_APPLY_RECEIPT,
  "plan_delegates_open_decision_to_worker_rejected",
]);

export function isCountedWorkerOutputRejection(receipt: string, code?: string): boolean {
  if (COUNTED_DONE_PATH_RECEIPTS.includes(receipt)) return true;
  if (receipt !== PROPOSAL_APPLY_RECEIPT || code === undefined) return false;
  return (COUNTED_PROPOSAL_REJECTION_CODES as readonly string[]).includes(code);
}

export interface CountedRejectionInput {
  store: MissionStore;
  retryState: RetryState;
  retryConfig: RetryConfig;
  now: Date;
  secretValues?: string[];
}

/**
 * Journal the receipt, then route: allow-listed ⇒ `task_failed` retry (or
 * `blocked` at the ceiling); anything else ⇒ escalate, counted_failures kept.
 */
export async function rejectWorkerOutput(
  input: CountedRejectionInput,
  receipt: string,
  reason: string,
  opts: { remoteId?: string; code?: string } = {},
): Promise<RejectionRoute> {
  await journalRejectionReceipt(input.store, receipt, opts.remoteId);
  if (!isCountedWorkerOutputRejection(receipt, opts.code)) {
    const blockedBy = HUMAN_ONLY_GOVERNANCE_RECEIPTS.includes(receipt) ? "governance" : "other";
    return escalate(input.retryState, reason, `${receipt}: ${reason}`, blockedBy); // prefix: audit F-3
  }
  const decision = recordAttemptOutcome(
    input.retryState,
    {
      category: "task_failed",
      evidence: `${receipt}: ${reason}`,
      secretValues: input.secretValues,
    },
    input.retryConfig,
    input.now,
  );
  return { kind: "retry_decision", decision };
}
